import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex } from '@rebass/grid';
import { Avatar, Button, Chip, IconButton, TextField, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PhotoCamera from '@material-ui/icons/PhotoCamera';
import InsertLink from '@material-ui/icons/InsertLink';
import NotificationsActive from '@material-ui/icons/NotificationsActive';
import Close from '@material-ui/icons/Close';
import SlidePage from '../organisim/slide-page';
import { colors, scrims } from '../../config/theme';

const styles = {
  posterAvatar: {
    width: '48px',
    height: '48px',
  },
  chip: {
    marginRight: '8px',
    background: colors.surfaceRaised,
    color: colors.textPrimary,
  },
  textField: {
    width: '100%',
  },
  titleInput: {
    fontSize: '20px',
    fontWeight: 600,
  },
  bodyInput: {
    fontSize: '16px',
    lineHeight: 1.5,
  },
  photoPreview: {
    position: 'relative',
    display: 'inline-block',
  },
  removePhotoButton: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    background: scrims.strong,
  },
  footer: {
    position: 'sticky',
    bottom: 0,
    background: colors.surface,
    paddingTop: '12px',
    paddingBottom: '12px',
  },
};

// These chips fill with the brand colour when active, so their label and icon
// have to flip to the on-primary colour with them rather than staying put.
const chipFill = (active) => ({
  background: active ? colors.primary : colors.surfaceRaised,
  color: active ? colors.onPrimary : colors.textPrimary,
});

const chipIconFill = (active) => ({
  color: active ? colors.onPrimary : colors.textPrimary,
});

const PostFormSheet = ({
  classes,
  open,
  onClose,
  mode,
  draft,
  setDraft,
  onSubmit,
  isSubmitting,
  isUploadingImage,
  onImageUpload,
  error,
  authorName,
  authorAvatarUrl,
}) => {
  const isEdit = mode === 'edit';
  const fileInputRef = useRef(null);
  const [showLinks, setShowLinks] = useState(false);

  useEffect(() => {
    if (open) {
      setShowLinks(!!draft.linksText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <SlidePage open={open} onClose={onClose} title={isEdit ? 'Edit update' : 'Post an update'}>
      <Flex alignItems="center" mb="16px">
        <Avatar src={authorAvatarUrl} className={classes.posterAvatar}>
          {(authorName || 'G').charAt(0).toUpperCase()}
        </Avatar>
        <Box ml="12px">
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            {authorName || 'Gym staff'}
          </Typography>
        </Box>
      </Flex>

      <Flex mb="16px">
        <Chip
          icon={
            <NotificationsActive
              fontSize="small"
              style={chipIconFill(draft.notify_users)}
            />
          }
          label="Notify members"
          clickable
          onClick={() => setDraft({ ...draft, notify_users: !draft.notify_users })}
          className={classes.chip}
          style={chipFill(draft.notify_users)}
        />
        <Chip
          icon={<InsertLink fontSize="small" style={chipIconFill(showLinks)} />}
          label="Add links"
          clickable
          onClick={() => setShowLinks((prev) => !prev)}
          className={classes.chip}
          style={chipFill(showLinks)}
        />
      </Flex>

      <TextField
        placeholder="Title"
        value={draft.title}
        onChange={(event) => setDraft({ ...draft, title: event.target.value })}
        className={classes.textField}
        variant="standard"
        InputProps={{ disableUnderline: true, classes: { input: classes.titleInput } }}
      />
      <Box mt="8px">
        <TextField
          placeholder="What should members know?"
          value={draft.body}
          onChange={(event) => setDraft({ ...draft, body: event.target.value })}
          className={classes.textField}
          variant="standard"
          InputProps={{ disableUnderline: true, classes: { input: classes.bodyInput } }}
          multiline
          minRows={8}
        />
      </Box>

      {showLinks && (
        <Box mt="16px">
          <TextField
            label="Links (one per line or comma separated)"
            value={draft.linksText}
            onChange={(event) => setDraft({ ...draft, linksText: event.target.value })}
            className={classes.textField}
            multiline
            rows={2}
          />
        </Box>
      )}

      {!!draft.image_url && (
        <Box mt="16px" className={classes.photoPreview}>
          <img
            src={draft.image_url}
            alt="News post"
            style={{ maxWidth: '100%', maxHeight: '220px', borderRadius: '8px', display: 'block' }}
          />
          <IconButton
            className={classes.removePhotoButton}
            size="small"
            onClick={() => setDraft({ ...draft, image_url: '' })}
          >
            <Close fontSize="small" style={{ color: colors.onScrim }} />
          </IconButton>
        </Box>
      )}

      {error && (
        <Box mt="16px">
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onImageUpload}
        style={{ display: 'none' }}
      />

      <Flex justifyContent="space-between" alignItems="center" className={classes.footer} mt="24px">
        <Chip
          icon={<PhotoCamera fontSize="small" style={{ color: colors.textPrimary }} />}
          label={
            isUploadingImage ? 'Uploading...' : draft.image_url ? 'Replace photo' : 'Add photo'
          }
          clickable
          disabled={isUploadingImage}
          onClick={() => fileInputRef.current?.click()}
          className={classes.chip}
        />
        <Button
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          onClick={onSubmit}
        >
          {isSubmitting ? (isEdit ? 'Saving...' : 'Posting...') : isEdit ? 'Save' : 'Post'}
        </Button>
      </Flex>
    </SlidePage>
  );
};

export default withStyles(styles)(PostFormSheet);
